import { useState, useContext, useEffect } from "react";
import "./Navbar.css";
import { NavbarData } from "../../Data";
import Badge from "@mui/material/Badge";
import { Menu, Dropdown, Popover } from "antd"; // Import Popover from antd
import { Avatar } from "@mui/material";
import { ApiContext } from "../../Context/ApiProvider";
import ProductCards from "../ProductCards/ProductCards";
import SearchProduct from "../SearchProduct/SearchProduct";

const Navbar = ({ className }) => {
	const { userData, cartItems, products, handleLogout, orderData } =
		useContext(ApiContext);

	const [searchInput, setSearchInput] = useState("");
	const [menuVisible, setMenuVisible] = useState(false);
	const [searchResults, setSearchResults] = useState([]);
	const [hover, setHover] = useState(false);
	const [popoverVisible, setPopoverVisible] = useState(false); // Manage popover visibility

	// Debounce for search input (wait before searching)
	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			if (searchInput) {
				const filteredProducts = products.filter(
					(product) =>
						product.name.toLowerCase().includes(searchInput.toLowerCase()) ||
						product.briefDescription
							.toLowerCase()
							.includes(searchInput.toLowerCase())
				);
				setSearchResults(filteredProducts);
				setPopoverVisible(filteredProducts.length > 0); // Show popover if results exist
			} else {
				setSearchResults([]); // Clear results if searchInput is empty
				setPopoverVisible(false); // Hide popover if there's no input
			}
		}, 300); // 300ms debounce delay

		return () => clearTimeout(delayDebounceFn); // Cleanup timeout
	}, [searchInput, products]);

	const handleMenuToggle = () => {
		setMenuVisible((prevVisible) => !prevVisible);
	};

	const personalItems = NavbarData?.filter((item) => item.type === "personal");
	const commonItems = NavbarData?.filter((item) => !item.type);

	const navigateTo = (path) => {
		window.location.href = path;
	};

	return (
		<div className={`Navbar ${className}`}>
			<div className="NavbarContainer">
				<div className="NavbarLogo">
					<a
						href="/"
						onClick={(e) => {
							e.preventDefault();
							navigateTo("/");
						}}
					>
						<img
							src="https://res.cloudinary.com/dvczs0agl/image/upload/v1727806313/MONGO_LOGO_qdbccb.png"
							alt="logo"
						/>
					</a>
					<div className="search-container">
						<Popover
							className="search-popover"
							content={
								searchResults.length > 0 ? (
									<ul className="search-results">
										{searchResults.map((product) => (
											<div
												key={product._id}
												onClick={() => {
													navigateTo(`/product/${product._id}`);
													setPopoverVisible(false);
												}}
												className="search-result"
											>
												<SearchProduct product={product} />
											</div>
										))}
									</ul>
								) : (
									<p>No results found</p>
								)
							}
							trigger="click"
							visible={popoverVisible}
							placement="bottom"
							arrow={false}
							onVisibleChange={(visible) => setPopoverVisible(visible)} // Sync popover visibility
						>
							<form onSubmit={(e) => e.preventDefault()}>
								<input
									className="search expandleft"
									id="searchleft"
									type="search"
									name="q"
									placeholder="Search for products"
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									onFocus={() => setHover(true)}
									onBlur={() => setHover(false)}
								/>
								<label className="button searchbutton" htmlFor="searchleft">
									<span className="mglass">&#9906;</span>
								</label>
							</form>
						</Popover>
					</div>
				</div>

				<div className={`Nav ${menuVisible ? "active" : ""}`}>
					<div className="NavLeft">
						<div className="NavbarLinks NavMobile">
							{commonItems?.map((item, index) => (
								<div className="NavItem" key={index}>
									{item.menu ? (
										<Dropdown
											overlay={
												<Menu>
													{item.menu.map((subItem, index) => (
														<Menu.Item key={index}>
															<a
																href={subItem.path}
																onClick={(e) => {
																	e.preventDefault();
																	navigateTo(subItem.path);
																}}
																style={{
																	display: "flex",
																	gap: "10px",
																}}
															>
																<div
																	className="subItemIcon"
																	style={{ color: "var(--coffee)" }}
																>
																	{subItem.icon}
																</div>
																{subItem.title}
															</a>
														</Menu.Item>
													))}
												</Menu>
											}
											placement="bottomCenter"
											trigger={["click"]}
											arrow
										>
											<a href={item.path} onClick={(e) => e.preventDefault()}>
												{item.icon}
												{item.title}
												<i className="fas fa-caret-down"></i>
											</a>
										</Dropdown>
									) : (
										<a
											href={item.path}
											onClick={(e) => {
												e.preventDefault();
												navigateTo(item.path);
											}}
										>
											{item.icon}
											{item.title}
										</a>
									)}
								</div>
							))}
						</div>
					</div>
					<div className="NavRight">
						<div className="NavbarLinks">
							{personalItems?.map((item, index) => (
								<div className="NavItem" key={index}>
									{item.title === "Account" ? (
										userData ? (
											<Dropdown
												overlay={
													<Menu>
														{item.menu.map((subItem, index) => (
															<Menu.Item key={index}>
																{subItem.title === "Logout" ? (
																	<div
																		style={{
																			display: "flex",
																			gap: "10px",
																			borderTop: "1px solid #777",
																		}}
																		onClick={handleLogout}
																	>
																		<div
																			className="subItemIcon"
																			style={{ color: "var(--coffee)" }}
																		>
																			{subItem.icon}
																		</div>
																		<div className="subItemTitle">
																			{subItem.title}
																		</div>
																	</div>
																) : (
																	<a
																		href={subItem.path}
																		onClick={(e) => {
																			e.preventDefault();
																			navigateTo(subItem.path);
																		}}
																		style={{
																			display: "flex",
																			gap: "10px",
																			alignItems: "center",
																		}}
																	>
																		<div
																			className="subItemIcon"
																			style={{ color: "var(--coffee)" }}
																		>
																			{subItem.icon}
																		</div>
																		<div className="subItemTitle">
																			{subItem.title}
																		</div>
																		{subItem.title === "Orders" && (
																			<Badge
																				badgeContent={
																					orderData?.filter(
																						(order) =>
																							order.orderStatus === "processing"
																					).length
																				}
																				color="primary"
																			/>
																		)}
																	</a>
																)}
															</Menu.Item>
														))}
													</Menu>
												}
												placement="bottomCenter"
												trigger={["click"]}
												arrow
											>
												<a href={item.path} onClick={(e) => e.preventDefault()}>
													{userData?.image ? (
														<Avatar
															src={userData?.image}
															alt={userData?.username}
															style={{ cursor: "pointer" }}
														/>
													) : (
														<i
															className="fas fa-user-circle"
															style={{ fontSize: "30px" }}
														></i>
													)}
													{userData?.username}{" "}
													<i className="fas fa-caret-down"></i>
												</a>
											</Dropdown>
										) : (
											<a
												href={item.path}
												onClick={(e) => {
													e.preventDefault();
													navigateTo(item.path);
												}}
											>
												{item.icon}
												{item.title}
											</a>
										)
									) : (
										<a
											href={item.path}
											onClick={(e) => {
												e.preventDefault();
												navigateTo(item.path);
											}}
										>
											{item.icon}
											{item.title}
											{item.title === "Cart" && cartItems?.length !== 0 && (
												<Badge
													badgeContent={cartItems?.length}
													color="primary"
												/>
											)}
										</a>
									)}
								</div>
							))}
						</div>
					</div>
				</div>
				<div className="MenuToggler">
					<i
						className={menuVisible ? "fas fa-times" : "fas fa-bars"}
						onClick={handleMenuToggle}
						style={{
							fontSize: "1.5rem",
							cursor: "pointer",
						}}
					></i>
				</div>
			</div>
		</div>
	);
};

export default Navbar;
